/**
 * PsicoSalud — Historial clínico electrónico (Módulo 2).
 *
 * Segundo flujo crítico cubierto por las pruebas de regresión. Permite
 * registrar consultas, agregar notas de sesión y solicitar el análisis
 * de apoyo sobre una nota cuando el paciente lo consintió.
 *
 * El timeline de encuentros es el elemento firma del sistema: cada
 * nodo representa una sesión, codificado por nivel de riesgo y marcado
 * con el punto de acento cuando hubo análisis con IA.
 */

import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import NoteAnalysisPanel from '@/components/NoteAnalysisPanel';
import EmptyState, { IconNotebook } from '@/components/EmptyState';
import Skeleton from '@/components/Skeleton';
import PatientAppointments from '@/components/PatientAppointments';
import PatientScaleResults from '@/components/PatientScaleResults';
import { clinicalRecordsApi, patientsApi } from '@/api';
import { ApiError } from '@/api/client';
import { useToast } from '@/context/ToastContext';
import { useStickyShrink } from '@/hooks/useStickyShrink';
import type { ClinicalRecord, NlpAnalysis, NoteType, Patient, RecordType, RiskLevel } from '@/types';

const RECORD_TYPES: RecordType[] = ['INITIAL_EVAL', 'FOLLOW_UP', 'DISCHARGE', 'CRISIS'];
const NOTE_TYPES: NoteType[] = ['PROGRESS', 'SOAP', 'DAP', 'BIRP'];
const RISK_LEVELS: RiskLevel[] = ['LOW', 'MODERATE', 'HIGH', 'CRITICAL'];

const RISK_BADGE: Record<RiskLevel, string> = {
  LOW: 'badge-success',
  MODERATE: 'badge-info',
  HIGH: 'badge-warning',
  CRITICAL: 'badge-critical',
};

const RISK_ENCUENTRO_CLASS: Record<RiskLevel, string> = {
  LOW: 'is-low-risk',
  MODERATE: 'is-moderate',
  HIGH: 'is-high-risk',
  CRITICAL: 'is-critical',
};

export default function ClinicalRecordPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { patientId = '' } = useParams();
  const { sentinelRef, isScrolled } = useStickyShrink<HTMLDivElement>();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [records, setRecords] = useState<ClinicalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [analyses, setAnalyses] = useState<Record<string, NlpAnalysis>>({});
  const [analyzingNote, setAnalyzingNote] = useState<string | null>(null);

  const [recordForm, setRecordForm] = useState({
    record_type: 'FOLLOW_UP' as RecordType,
    chief_complaint: '',
    diagnosis: '',
    treatment_plan: '',
    observations: '',
    risk_level: '' as '' | RiskLevel,
    session_date: new Date().toISOString().slice(0, 10),
  });

  const [noteDrafts, setNoteDrafts] = useState<
    Record<string, { content: string; note_type: NoteType; consent: boolean }>
  >({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, history] = await Promise.all([
        patientsApi.get(patientId),
        clinicalRecordsApi.patientHistory(patientId),
      ]);
      setPatient(detail);
      setRecords(history);
    } catch {
      toast({ type: 'error', message: t('errors.network') });
    } finally {
      setLoading(false);
    }
  }, [patientId, t, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreateRecord = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await clinicalRecordsApi.create({
        patient_id: patientId,
        record_type: recordForm.record_type,
        chief_complaint: recordForm.chief_complaint || undefined,
        diagnosis: recordForm.diagnosis || undefined,
        treatment_plan: recordForm.treatment_plan || undefined,
        observations: recordForm.observations || undefined,
        risk_level: recordForm.risk_level || undefined,
        session_date: new Date(`${recordForm.session_date}T12:00:00`).toISOString(),
      });
      toast({ type: 'success', message: t('record.created') });
      setShowForm(false);
      await load();
    } catch (error) {
      toast({
        type: 'error',
        message: error instanceof ApiError ? error.message : t('errors.generic'),
      });
    }
  };

  const handleAddNote = async (recordId: string) => {
    const draft = noteDrafts[recordId];
    if (!draft?.content.trim()) return;

    try {
      await clinicalRecordsApi.addNote(recordId, {
        content: draft.content,
        note_type: draft.note_type,
        ai_analysis_consent: draft.consent,
      });
      setNoteDrafts((current) => ({ ...current, [recordId]: { ...draft, content: '' } }));
      toast({ type: 'success', message: t('record.noteAdded') });
      await load();
    } catch (error) {
      toast({
        type: 'error',
        message: error instanceof ApiError ? error.message : t('errors.generic'),
      });
    }
  };

  const handleAnalyze = async (recordId: string, noteId: string) => {
    setAnalyzingNote(noteId);
    try {
      const response = await clinicalRecordsApi.analyzeNote(recordId, noteId);
      setAnalyses((current) => ({ ...current, [noteId]: response.results }));
      toast({
        type: 'success',
        title: t('record.analyzeCompleteTitle'),
        message: t('record.analyzeCompleteMessage'),
      });
    } catch (error) {
      // El 403 más frecuente es la falta de consentimiento vigente:
      // conviene decirlo con el mensaje del backend, que ya explica cómo
      // resolverlo desde la ficha del paciente.
      toast({
        type: 'error',
        message: error instanceof ApiError ? error.message : t('analytics.serviceUnavailable'),
      });
    } finally {
      setAnalyzingNote(null);
    }
  };

  const draftFor = (recordId: string) =>
    noteDrafts[recordId] ?? { content: '', note_type: 'PROGRESS' as NoteType, consent: false };

  const updateDraft = (recordId: string, patch: Partial<ReturnType<typeof draftFor>>) =>
    setNoteDrafts((current) => ({ ...current, [recordId]: { ...draftFor(recordId), ...patch } }));

  if (loading) {
    return (
      <>
        <header className="page-header with-flex" aria-hidden="true">
          <div>
            <span className="page-eyebrow">{t('record.title')}</span>
            <h1>{t('record.title')}</h1>
            <p className="page-descripcion">
              <Skeleton variant="text" width="40%" />
            </p>
          </div>
        </header>

        <div className="card">
          <div className="timeline-cabecera">
            <h3>{t('record.timeline.title')}</h3>
          </div>
          <Skeleton.TimelineItems count={4} />
        </div>
      </>
    );
  }

  const sortedRecords = [...records].sort(
    (a, b) => new Date(b.session_date).getTime() - new Date(a.session_date).getTime(),
  );

  return (
    <>
      <header
        className={`page-header with-flex is-sticky-shrink${isScrolled ? ' is-scrolled' : ''}`}
      >
        <div>
          <span className="page-eyebrow">{t('record.title')}</span>
          <h1>{t('record.title')}</h1>
          <p className="page-descripcion">
            <Link
              to={`/pacientes/${patientId}`}
              style={{ color: 'var(--principal)', fontWeight: 'var(--peso-medio)' }}
            >
              {patient?.full_name}
            </Link>
            <span className="t-numerico"> · {patient?.document_id}</span>
          </p>
        </div>
        <div className="page-acciones">
          <button
            onClick={() => setShowForm((open) => !open)}
            data-cy="record-toggle-form"
          >
            {showForm ? t('common.cancel') : t('record.newRecord')}
          </button>
        </div>
      </header>

      <div
        ref={sentinelRef}
        aria-hidden="true"
        style={{ height: 1, marginTop: -1, pointerEvents: 'none' }}
      />

      <div className="notice notice-info t-pequeno" data-cy="record-encrypted-notice">
        {t('record.encrypted')}
      </div>

      {/* ============================================================
          Timeline de encuentros (elemento firma).
          ============================================================ */}
      <div className="card">
        <div className="timeline-cabecera">
          <h3>{t('record.timeline.title')}</h3>
          <span className="timeline-resumen">
            <strong>{sortedRecords.length}</strong>{' '}
            {sortedRecords.length === 1 ? 'sesión' : 'sesiones'}
          </span>
        </div>

        {sortedRecords.length === 0 ? (
          <p className="empty-state" data-cy="record-timeline-empty">
            {t('record.timeline.noSessions')}
          </p>
        ) : (
          <ol className="encounters-timeline" data-cy="encounters-timeline">
            {sortedRecords.map((record) => {
              const hasAi = record.notes.some((note) => note.ai_analysis_results);
              const riskClass = record.risk_level ? RISK_ENCUENTRO_CLASS[record.risk_level] : '';
              const aiClass = hasAi ? ' is-ai-analyzed' : '';
              const riskBadge = record.risk_level
                ? RISK_BADGE[record.risk_level]
                : 'badge-neutral';

              return (
                <li
                  key={record.id}
                  className={`encounter${riskClass ? ` ${riskClass}` : ''}${aiClass}`}
                  data-cy={`encounter-${record.id}`}
                >
                  <div className="encuentro-fecha">
                    <span>
                      {new Date(record.session_date).toLocaleDateString('es-PY', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                    {record.risk_level && (
                      <span className={`badge ${riskBadge}`}>
                        {t('record.riskLevel')}: {t(`record.riskLevels.${record.risk_level}`)}
                      </span>
                    )}
                  </div>
                  <div className="encuentro-tipo">
                    {t(`record.types.${record.record_type}`)}
                    {record.notes.length > 0 && (
                      <span
                        className="t-pequeno t-suave"
                        style={{ fontWeight: 'var(--peso-regular)' }}
                      >
                        · {record.notes.length}{' '}
                        {record.notes.length === 1
                          ? t('record.notes').toLowerCase()
                          : t('record.notes').toLowerCase()}
                      </span>
                    )}
                  </div>
                  {record.chief_complaint && (
                    <p className="t-pequeno" style={{ marginBottom: 'var(--espacio-2)' }}>
                      <strong>{t('record.chiefComplaint')}:</strong> {record.chief_complaint}
                    </p>
                  )}
                  {hasAi && (
                    <div className="encuentro-ai">{t('record.timeline.aiAnalyzed')}</div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {showForm && (
        <div className="card">
          <div className="card-header">
            <h2>{t('record.newRecord')}</h2>
          </div>
          <form onSubmit={handleCreateRecord} data-cy="record-form">
            <div className="form-row">
              <div className="field">
                <label htmlFor="record_type">{t('record.recordType')}</label>
                <select
                  id="record_type"
                  value={recordForm.record_type}
                  onChange={(e) =>
                    setRecordForm({ ...recordForm, record_type: e.target.value as RecordType })
                  }
                  data-cy="record-type"
                >
                  {RECORD_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(`record.types.${type}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="session_date">{t('record.sessionDate')}</label>
                <input
                  id="session_date"
                  type="date"
                  required
                  value={recordForm.session_date}
                  onChange={(e) => setRecordForm({ ...recordForm, session_date: e.target.value })}
                  data-cy="record-date"
                />
              </div>
              <div className="field">
                <label htmlFor="risk_level">{t('record.riskLevel')}</label>
                <select
                  id="risk_level"
                  value={recordForm.risk_level}
                  onChange={(e) =>
                    setRecordForm({ ...recordForm, risk_level: e.target.value as '' | RiskLevel })
                  }
                  data-cy="record-risk"
                >
                  <option value="">—</option>
                  {RISK_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {t(`record.riskLevels.${level}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label htmlFor="chief_complaint">{t('record.chiefComplaint')}</label>
              <input
                id="chief_complaint"
                value={recordForm.chief_complaint}
                onChange={(e) => setRecordForm({ ...recordForm, chief_complaint: e.target.value })}
                data-cy="record-complaint"
              />
            </div>

            <div className="field">
              <label htmlFor="diagnosis">{t('record.diagnosis')}</label>
              <input
                id="diagnosis"
                value={recordForm.diagnosis}
                onChange={(e) => setRecordForm({ ...recordForm, diagnosis: e.target.value })}
              />
            </div>

            <div className="field">
              <label htmlFor="treatment_plan">{t('record.treatmentPlan')}</label>
              <textarea
                id="treatment_plan"
                value={recordForm.treatment_plan}
                onChange={(e) => setRecordForm({ ...recordForm, treatment_plan: e.target.value })}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="secondary" onClick={() => setShowForm(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" data-cy="record-submit">
                {t('common.save')}
              </button>
            </div>
          </form>
        </div>
      )}

      {sortedRecords.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<IconNotebook />}
            title={t('record.emptyTitle')}
            description={t('record.emptyDescription')}
            action={{
              label: t('record.newRecord'),
              onClick: () => setShowForm(true),
            }}
          />
        </div>
      ) : (
        <div data-cy="records-stack">
          {sortedRecords.map((record) => {
            const draft = draftFor(record.id);
            return (
              <div className="card" key={record.id} data-cy="record-card">
                <div className="card-header">
                  <div>
                    <span className="card-eyebrow">
                      {t(`record.types.${record.record_type}`)}
                    </span>
                    <h2>
                      {new Date(record.session_date).toLocaleDateString('es-PY', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </h2>
                  </div>
                  {record.risk_level && (
                    <span className={`badge ${RISK_BADGE[record.risk_level]}`}>
                      {t('record.riskLevel')}: {t(`record.riskLevels.${record.risk_level}`)}
                    </span>
                  )}
                </div>

                {record.chief_complaint && (
                  <p>
                    <strong>{t('record.chiefComplaint')}:</strong> {record.chief_complaint}
                  </p>
                )}
                {record.diagnosis && (
                  <p>
                    <strong>{t('record.diagnosis')}:</strong> {record.diagnosis}
                  </p>
                )}
                {record.treatment_plan && (
                  <p>
                    <strong>{t('record.treatmentPlan')}:</strong> {record.treatment_plan}
                  </p>
                )}

                <h3 className="t-mayuscula" style={{ color: 'var(--tinta-suave)' }}>
                  {t('record.notes')}
                </h3>
                {record.notes.length === 0 ? (
                  <p className="t-pequeno t-suave">{t('common.empty')}</p>
                ) : (
                  record.notes.map((note) => (
                    <div
                      key={note.id}
                      style={{ marginBottom: 'var(--espacio-4)' }}
                      data-cy="record-note"
                    >
                      <div className="t-pequeno t-suave">
                        <strong>{t(`record.noteTypes.${note.note_type}`)}</strong> ·{' '}
                        {new Date(note.created_at).toLocaleString('es-PY')}
                      </div>
                      <p style={{ whiteSpace: 'pre-wrap', margin: '0.3rem 0' }}>
                        {note.content}
                      </p>

                      <button
                        className="secondary is-pequeno"
                        disabled={analyzingNote === note.id}
                        onClick={() => void handleAnalyze(record.id, note.id)}
                        data-cy="record-analyze"
                      >
                        {analyzingNote === note.id ? t('record.analyzing') : t('record.analyze')}
                      </button>

                      {(analyses[note.id] ?? note.ai_analysis_results) && (
                        <NoteAnalysisPanel
                          analysis={(analyses[note.id] ?? note.ai_analysis_results)!}
                        />
                      )}
                    </div>
                  ))
                )}

                <div className="field">
                  <label htmlFor={`note-${record.id}`}>{t('record.addNote')}</label>
                  <textarea
                    id={`note-${record.id}`}
                    value={draft.content}
                    onChange={(e) => updateDraft(record.id, { content: e.target.value })}
                    data-cy="record-note-content"
                  />
                </div>

                <div className="form-row">
                  <div className="field">
                    <label htmlFor={`note-type-${record.id}`}>{t('record.noteType')}</label>
                    <select
                      id={`note-type-${record.id}`}
                      value={draft.note_type}
                      onChange={(e) => updateDraft(record.id, { note_type: e.target.value as NoteType })}
                    >
                      {NOTE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {t(`record.noteTypes.${type}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div
                    className="field"
                    style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 'var(--espacio-2)' }}
                  >
                    <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 400 }}>
                      <input
                        type="checkbox"
                        checked={draft.consent}
                        onChange={(e) => updateDraft(record.id, { consent: e.target.checked })}
                        data-cy="record-note-consent"
                      />
                      <span className="t-pequeno">{t('record.aiConsent')}</span>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    onClick={() => void handleAddNote(record.id)}
                    disabled={!draft.content.trim()}
                    data-cy="record-note-submit"
                  >
                    {t('record.addNote')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {patient && <>
        <PatientAppointments key={`appointments-${patientId}`} patientId={patientId} />
        <PatientScaleResults key={`scales-${patientId}`} patientId={patientId} records={records} />
      </>}
    </>
  );
}
